import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { formatGel, formatNumber, formatRate } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { Card as CardData } from '../lib/queries';
import { Logo } from './ui';

const TIER_SKIN: Record<string, { ring: string; glow: string; text: string }> = {
  BRONZE: { ring: 'ring-bronze/30', glow: 'from-bronze/18', text: 'text-bronze' },
  SILVER: { ring: 'ring-silver/30', glow: 'from-silver/18', text: 'text-silver' },
  GOLD: { ring: 'ring-gold/30', glow: 'from-gold/18', text: 'text-gold' },
  PLATINUM: { ring: 'ring-platinum/35', glow: 'from-platinum/20', text: 'text-platinum' },
};

export function LoyaltyCard({ card }: { card: CardData }) {
  const { t, language } = useI18n();
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const skin = TIER_SKIN[card.tier.code] ?? TIER_SKIN['BRONZE']!;

  useEffect(() => {
    let alive = true;
    void QRCode.toDataURL(card.qr.token, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 512,
      color: { dark: '#050506', light: '#f4f4f7' },
    }).then((url) => { if (alive) setQrUrl(url); });
    return () => { alive = false; };
  }, [card.qr.token]);

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-surface ring-1 ${skin.ring}`}>
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b ${skin.glow} to-transparent`} />

      <div className="relative p-5">
        <div className="flex items-start justify-between gap-4">
          <Logo size="md" />
          <div className="text-right">
            <div className={`text-[0.7rem] font-bold tracking-[0.22em] ${skin.text}`}>
              {card.tier.code}
            </div>
            {card.tier.isPermanent && (
              <div className="mt-0.5 text-[0.58rem] tracking-[0.14em] text-ink-faint">
                {t('card.permanent')}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5">
          <div className="text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-ink-faint">
            {t('app.name')}
          </div>
          <div className="mt-1 font-display text-2xl font-bold tracking-tight text-ink">
            {card.name}
          </div>
          <div className={`tabular mt-1 text-sm font-semibold ${skin.text}`}>
            {formatRate(card.tier.cashbackRateBp)} {t('card.cashbackRate')}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-line bg-raised/60 px-4 py-3">
            <div className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
              {t('card.cashback')}
            </div>
            <div className="tabular mt-1 text-xl font-bold text-brand">
              {formatGel(card.cashbackAvailableTetri, language)}
            </div>
            {card.cashbackPendingTetri > 0 && (
              <div className="mt-0.5 text-[0.62rem] text-ink-faint">
                +{formatGel(card.cashbackPendingTetri, language)} {t('card.pending')}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-line bg-raised/60 px-4 py-3">
            <div className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-ink-faint">
              {t('card.points')}
            </div>
            <div className="tabular mt-1 text-xl font-bold text-ink">
              {formatNumber(card.pointsBalance, language)}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-col items-center border-t border-line pt-5">
          <div className="rounded-2xl bg-ink p-3">
            {qrUrl ? (
              <img src={qrUrl} alt="" className="h-40 w-40" />
            ) : (
              <div className="h-40 w-40 animate-pulse rounded-lg bg-muted" />
            )}
          </div>
          <div className="tabular mt-3 text-sm font-semibold tracking-[0.18em] text-ink-dim">
            {card.loyaltyId}
          </div>
          <div className="mt-1 text-[0.68rem] text-ink-faint">{t('card.showQr')}</div>
          <div className="mt-0.5 text-[0.6rem] text-ink-faint/70">{t('card.qrRefresh')}</div>
        </div>
      </div>
    </div>
  );
}
